import { MatterActivityFeedActions } from '@/matter/activity-feed/store/slice'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PlusAddCircle } from '@/assets/icons'
import { Header } from '@/components/header/Header'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectMatterById } from '@/matter/store/selectors'
import { isActiveMatterMember } from '@/matter/utils/matter-permissions'
import { matterPaths } from '@/routing/paths'
import { FileStagingPage } from '@/matter-asset-common/files/components/FileStagingPage'
import { MatterRecordStagedFileCard } from '@/matter-asset-common/files/components/MatterRecordStagedFileCard'
import { StagedMatterFilesDialog } from '@/matter-asset-common/files/components/StagedMatterFilesDialog'
import { StagedFileStatus } from '@/matter-asset-common/files/utils/get-staged-file-card-state'
import { ADD_FILES_MODAL_TAB, type AddFilesModalInitialTab } from '@/chat-common/components/source-dialogs/AddFilesModal'
import { useTableCreationFiles } from '../hooks/useTableCreationFiles'
import { postCreateTable } from '../fetch/post-create-table'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { TableCreationMethod } from '@/analytics/enums/table.enums'
import { trackTableCreated } from '@/analytics/utils/table-analytics'
import { BottomStickyBar } from '@/user_files/drive/components/BottomStickyBar'

export default function CreateTablePage() {
  const { matterId } = useParams<{ matterId: string }>()
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))
  if (!matterId || !isActiveMatterMember(matter)) return <p className="p-6">This matter is not available.</p>
  return <CreateTableForm key={matterId} matterId={matterId} matterName={matter?.name ?? 'Matter'} />
}

function CreateTableForm({ matterId, matterName }: { matterId: string; matterName: string }) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [createdTableId, setCreatedTableId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pickerTab, setPickerTab] = useState<AddFilesModalInitialTab | null>(null)
  const submitting = useRef(false)
  const files = useTableCreationFiles(matterId, !saving)
  useEffect(() => {
    // Keep folder preparation and conflict prompts mounted until files reach the
    // shared upload queue. Uploading and document processing do not delay navigation.
    if (createdTableId && !files.upload.preparing) navigate(matterPaths.table(matterId, createdTableId))
  }, [createdTableId, files.upload.preparing, matterId, navigate])
  const readyCount = files.cards.filter((card) => card.ready).length
  const disabled = saving
  const create = async () => {
    if (disabled || submitting.current) return
    submitting.current = true
    setSaving(true)
    setError(null)
    try {
      const submittedDocumentIds = files.documentIds
      const table = await postCreateTable(matterId, name.trim() || 'Untitled table', true, submittedDocumentIds)
      trackTableCreated(trackEvent, {
        tableId: table.id,
        matterId,
        tableCreationMethod: TableCreationMethod.Files,
        documentCount: submittedDocumentIds.length,
      })
      dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
      files.attachUploadsToTable(table.id, submittedDocumentIds)
      setCreatedTableId(table.id)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to create table.')
      submitting.current = false
      setSaving(false)
    }
  }
  return (
    <div className="grow flex flex-col w-full h-full overflow-hidden">
      <Header
        borderBottom
        breadcrumbs={[
          { id: matterId, label: matterName, onClick: () => navigate(matterPaths.overview(matterId)) },
          { id: 'tables', label: 'Tables', onClick: () => navigate(matterPaths.tables(matterId)) },
          { id: 'create-table', label: 'Create table' },
        ]}
      />
      <FileStagingPage
        matterId={matterId}
        upload={files.upload}
        title="Create table"
        description="Select which files to include, then name your table."
        uploadInformation={<BottomStickyBar inline />}
        headerExtra={
          <input
            aria-label="Table name (optional)"
            placeholder="Table Name (Optional)"
            value={name}
            disabled={saving}
            onChange={(event) => setName(event.target.value)}
            className="w-full rounded border-0 ring-1 ring-inset ring-brand-neutral-400 bg-white px-3 py-4 text-base text-brand-neutral-950 placeholder:text-brand-neutral-600 focus:ring-brand-400 focus:ring-inset focus:outline-hidden"
          />
        }
        totalStaged={files.cards.length}
        readyStagedCount={readyCount}
        failedStagedCount={files.cards.filter((card) => card.status === StagedFileStatus.ERROR).length}
        onOpenFilePicker={setPickerTab}
        onClearAll={files.clear}
        onSubmit={() => void create()}
        submitDisabled={disabled}
        submitPending={saving}
        submitLabel="Create Table"
        pendingLabel="Creating…"
        error={error}
      >
        {files.cards.map((card) => (
          <MatterRecordStagedFileCard
            key={card.id}
            fileName={card.fileName}
            inProgressLabel={card.status === StagedFileStatus.UPLOADING ? 'Uploading...' : card.status === StagedFileStatus.PROCESSING ? 'Processing...' : null}
            disposition={card.ready ? { text: 'New file · Ready to process', className: 'text-green-800', Icon: PlusAddCircle } : null}
            errorMessage={card.errorMessage || undefined}
            removeDisabled={saving}
            onRemove={() => files.remove(card.id)}
          />
        ))}
      </FileStagingPage>
      <StagedMatterFilesDialog
        includeProcessingFiles
        pdfOnly={false}
        open={pickerTab !== null}
        matterId={matterId}
        initialSelectedIds={files.documentIds}
        initialTab={pickerTab ?? ADD_FILES_MODAL_TAB.MATTER_FILES}
        onClose={() => setPickerTab(null)}
        onApply={files.selectFiles}
      />
    </div>
  )
}
